"use client";

import { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  studentLogin,
  studentRegister,
  studentLogout,
  getStudentUser,
  isStudent,
  normalizePhone,
  PHONE_PATTERN,
  type StudentUser,
} from "@/lib/student-auth";
import { motion } from "framer-motion";
import { UserCircle, Mail, Lock, Eye, EyeOff, ArrowLeft, User, Phone } from "lucide-react";

function LoginForm() {
  const router       = useRouter();
  const searchParams = useSearchParams();
  const redirectTo   = searchParams?.get("redirect") || "/student/dashboard-redirect";

  const [mode, setMode]           = useState<"login" | "signup">("login");
  const [name, setName]           = useState("");
  const [phone, setPhone]         = useState("");
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw]       = useState(false);
  const [showCpw, setShowCpw]     = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [existing, setExisting]   = useState<StudentUser | null>(null);

  // Leftover website sessions used to skip this form, locking test accounts
  // in. Offer continue vs switch instead of an automatic redirect.
  useEffect(() => {
    const forceSwitch = searchParams?.get("switch") === "1";
    if (forceSwitch) {
      studentLogout();
      setExisting(null);
      return;
    }
    const user = getStudentUser();
    if (user && isStudent(user)) setExisting(user);
  }, [searchParams]);

  // Reset confirm-password when switching modes
  const switchMode = (next: "login" | "signup") => {
    setMode(next);
    setError("");
    setConfirmPw("");
    setPassword("");
    setPhone("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // ── Signup-specific validation ──────────────────────────────────────
    if (mode === "signup") {
      if (!name.trim() || !email.trim() || !phone.trim() || !password || !confirmPw) {
        setError("Validation failed: all fields are required.");
        return;
      }
      if (!PHONE_PATTERN.test(normalizePhone(phone))) {
        setError("Validation failed: enter a valid phone number (10–15 digits).");
        return;
      }
      if (password.length < 8) {
        setError("Validation failed: password must be at least 8 characters.");
        return;
      }
      if (password !== confirmPw) {
        setError("Validation failed: passwords do not match.");
        return;
      }
    }

    setLoading(true);
    try {
      const user =
        mode === "login"
          ? await studentLogin(email, password)
          : await studentRegister(name, email, password, phone);

      if (!isStudent(user)) {
        setError(
          "This portal is for students only. Admins/trainers please use the admin panel.",
        );
        setLoading(false);
        return;
      }
      router.push(redirectTo);
    } catch (err: any) {
      const msg: string = err.message || "Something went wrong. Please try again.";
      // Translate common backend messages into user-friendly ones
      if (msg.toLowerCase().includes("already exists")) {
        setError("Validation failed: this email is already registered. Try logging in.");
      } else {
        setError(msg);
      }
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border/60 bg-card/80 p-8 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.4)] backdrop-blur-xl dark:border-white/10">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-mont-violet to-purple-600 shadow-[0_0_30px_rgba(139,92,246,0.4)]">
          <UserCircle className="h-8 w-8 text-white" />
        </div>
        <h1 className="text-2xl font-extrabold text-foreground font-heading">
          {mode === "login" ? "Student Login" : "Create Account"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {mode === "login"
            ? "Access your student dashboard"
            : "Join Mont2 Academy as a student"}
        </p>
      </div>

      {existing && (
        <div className="mb-6 rounded-xl border border-mont-violet/30 bg-mont-violet/10 p-4">
          <p className="text-sm text-muted-foreground">Currently signed in</p>
          <p className="mt-0.5 font-semibold text-foreground">{existing.name}</p>
          <p className="text-xs text-muted-foreground">{existing.email}</p>
          <div className="mt-4 flex flex-col gap-2">
            <Button
              type="button"
              onClick={() => router.replace(redirectTo)}
              className="h-11 w-full rounded-xl bg-gradient-to-r from-mont-violet to-purple-500 font-bold text-white"
            >
              Continue
            </Button>
            <button
              type="button"
              onClick={() => {
                studentLogout();
                setExisting(null);
                setError("");
                router.replace("/student/login?switch=1");
              }}
              className="h-11 w-full rounded-xl border border-border/60 text-sm font-semibold text-muted-foreground hover:text-foreground"
            >
              Use a different account
            </button>
          </div>
        </div>
      )}

      {/* Mode tabs */}
      <div className="mb-6 flex rounded-xl border border-border/60 bg-muted/30 p-1">
        <button
          onClick={() => switchMode("login")}
          className={`flex-1 rounded-lg py-2.5 text-sm font-bold transition-all ${
            mode === "login"
              ? "bg-mont-violet text-white shadow-[0_0_16px_rgba(139,92,246,0.4)]"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >Login</button>
        <button
          onClick={() => switchMode("signup")}
          className={`flex-1 rounded-lg py-2.5 text-sm font-bold transition-all ${
            mode === "signup"
              ? "bg-mont-violet text-white shadow-[0_0_16px_rgba(139,92,246,0.4)]"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >Sign Up</button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Full Name (signup only) */}
        {mode === "signup" && (
          <div className="relative">
            <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Full Name"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              className="h-12 pl-10 rounded-xl border-border/60 bg-background/60 focus-visible:ring-mont-violet"
            />
          </div>
        )}

        {/* Email */}
        <div className="relative">
          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            className="h-12 pl-10 rounded-xl border-border/60 bg-background/60 focus-visible:ring-mont-violet"
          />
        </div>

        {/* Phone Number (signup only) */}
        {mode === "signup" && (
          <div className="relative">
            <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="tel"
              inputMode="tel"
              placeholder="Phone number"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              required
              className={`h-12 pl-10 rounded-xl border-border/60 bg-background/60 focus-visible:ring-mont-violet ${
                phone && !PHONE_PATTERN.test(normalizePhone(phone)) ? "border-red-500/60" : ""
              }`}
            />
            {phone && !PHONE_PATTERN.test(normalizePhone(phone)) && (
              <p className="mt-1 text-xs text-red-400 pl-1">
                Enter 10–15 digits, optionally with a country code
              </p>
            )}
          </div>
        )}

        {/* Password */}
        <div className="relative">
          <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type={showPw ? "text" : "password"}
            placeholder="Password (min 8 chars)"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            minLength={8}
            className="h-12 pl-10 pr-12 rounded-xl border-border/60 bg-background/60 focus-visible:ring-mont-violet"
          />
          <button
            type="button"
            onClick={() => setShowPw(v => !v)}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        {/* Confirm Password (signup only) */}
        {mode === "signup" && (
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type={showCpw ? "text" : "password"}
              placeholder="Confirm Password"
              value={confirmPw}
              onChange={e => setConfirmPw(e.target.value)}
              required
              className={`h-12 pl-10 pr-12 rounded-xl border-border/60 bg-background/60 focus-visible:ring-mont-violet ${
                confirmPw && confirmPw !== password ? "border-red-500/60" : ""
              }`}
            />
            <button
              type="button"
              onClick={() => setShowCpw(v => !v)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showCpw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
            {/* Live mismatch hint */}
            {confirmPw && confirmPw !== password && (
              <p className="mt-1 text-xs text-red-400 pl-1">Passwords do not match</p>
            )}
          </div>
        )}

        {/* Error banner */}
        {error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-400"
          >
            {error}
          </motion.p>
        )}

        <Button
          type="submit"
          disabled={loading}
          className="h-12 w-full rounded-xl bg-gradient-to-r from-mont-violet to-purple-500 text-base font-bold text-white shadow-[0_4px_20px_rgba(139,92,246,0.4)] hover:opacity-90 hover:shadow-[0_4px_30px_rgba(139,92,246,0.6)]"
        >
          {loading
            ? "Please wait…"
            : mode === "login"
            ? existing
              ? "Sign in as another student"
              : "Login to Dashboard"
            : "Create Account"}
        </Button>
      </form>

      <p className="mt-6 text-center text-xs text-muted-foreground">
        {mode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
        <button
          onClick={() => switchMode(mode === "login" ? "signup" : "login")}
          className="font-semibold text-mont-violet hover:underline"
        >
          {mode === "login" ? "Sign up" : "Login"}
        </button>
      </p>

      <p className="mt-3 text-center text-xs text-muted-foreground/60">
        Admin/Trainer?{" "}
        <a
          href={process.env.NEXT_PUBLIC_ADMIN_URL || "https://admin.mont2academy.com"}
          target="_blank"
          rel="noopener noreferrer"
          className="text-mont-violet/70 hover:text-mont-violet"
        >
          Use Admin Panel →
        </a>
      </p>
    </div>
  );
}

export default function StudentLoginPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-16 relative overflow-hidden">
      {/* Background glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-mont-violet/10 blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] rounded-full bg-purple-500/8 blur-[80px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        <Link
          href="/"
          className="mb-8 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={16} /> Back to home
        </Link>

        <Suspense fallback={null}>
          <LoginForm />
        </Suspense>
      </motion.div>
    </div>
  );
}
